import { forwardRef } from "react";
import type { HTMLAttributes, ReactNode } from "react";
import "./Banner.css";

export type BannerTone = "success" | "attention" | "warning" | "error" | "info";

export interface BannerProps extends Omit<HTMLAttributes<HTMLDivElement>, "title"> {
  tone?: BannerTone;
  title?: ReactNode;
  icon?: ReactNode;
  onDismiss?: () => void;
  dismissLabel?: string;
}

const cx = (...classes: Array<string | false | undefined>) => classes.filter(Boolean).join(" ");

const CloseIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

export const Banner = forwardRef<HTMLDivElement, BannerProps>(function Banner(
  {
    tone = "info",
    title,
    icon,
    onDismiss,
    dismissLabel = "Dismiss",
    className,
    children,
    role = "status",
    ...rest
  },
  ref,
) {
  return (
    <div
      ref={ref}
      role={role}
      className={cx("go4-banner", `go4-banner--${tone}`, className)}
      {...rest}
    >
      {icon ? <span className="go4-banner__icon">{icon}</span> : null}
      <div className="go4-banner__content">
        {title ? <div className="go4-banner__title">{title}</div> : null}
        {children ? <div className="go4-banner__message">{children}</div> : null}
      </div>
      {onDismiss ? (
        <button
          type="button"
          aria-label={dismissLabel}
          className="go4-banner__close"
          onClick={onDismiss}
        >
          <CloseIcon />
        </button>
      ) : null}
    </div>
  );
});
